import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import makeStyles from '@mui/styles/makeStyles';
import { Button, Checkbox, CircularProgress, FormControlLabel, Stack, Switch } from '@mui/material';
import Divider from '@mui/material/Divider';
import MessageBox from '../../../util/messageBox';
import Grid from '@mui/material/Grid';
import ObservationTable from '../../../components/observation/observationTable';
import { ControlDependency, ControlDiscriminator, ControlPresentation } from '../../../controls/agyle/triageControls';
import { ChiefOfComplaint, Safeguarding } from '../../../controls/agyle/ecds';
import { CoreDataTextField } from '../../../components/core/textFieldWithCoreData';
import { calculateArrivalDate, PATIENT_TAGS } from '../../../util/patientUtil';
import { TextFieldDebounce } from '../../../controls/agyle/textFieldDebounce';
import { ControlDiagnosis, ControlInvestigations, ControlTreatment } from '../../../controls/agyle/diagnosis';
import { addObservationsToExaminationInput, mapObservationFromAPI, obsTypes } from './observation/obsUtils';
import API from '../../../app/api';
import _ from 'lodash';
import Formatter from '../../../util/formatter';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import PainScore from '../../../components/observation/painScore';
import { useDispatch, useSelector } from 'react-redux';
import { user } from '../../../store/reducers/user';
import { diagnosisBase } from './clerkingED/clerking';
import keyPerformanceIndicator from '../../../util/timeMarker';
import uuidv4 from 'uuid/v4';
import Notes from '../../../util/notes';
import { acuity, chiefComplaint, getEcdsVersion } from '../../../store/reducers/dicts';
import InformationBox from '../../../components/other/informationBox';
import useOpenState from '../../../hooks/useOpenState';
import AgyleDialog from '../../../components/modal/agyleDialog';
import { setSnackbar } from '../../../store/reducers/globals';
import useStickyState from '../../../hooks/useStickyState';
import { addNoteForPatient } from '../../../store/reducers/notes';
import { patientBanner } from '../../../controls/agyle/tasks/components/patientBanner';
import AgyleDatePicker from '../../../components/ui/fields/agyleDatePicker';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import AgyleStepper, { createStepObject } from '../../../components/ui/stepper/agyleStepper';
import AgyleLoadingSpinner from '../../../components/other/agyleLoadingSpinner';
import { getSafeguardingLevel, PATIENT_AGE_TYPE } from '../../../util/patientContextUtil';
import { getPatientTriageType, SEE_AND_TREAT_STATUSES } from './formsUtil';
import { triageBase } from './triage/triageUtils';
import { getConfig } from '../../../util/configUtil';
import { AgyleAssessmentIcon, AgyleSubmitIcon } from '../../../components/icons/agyle';
import DischargeForm from './discharge/dischargeForm';
import { ctxReferralFormData, openPatientForm, setDischargeTemplate } from '../../../store/reducers/patientContext';
import clsx from 'clsx';
import { obsIdToFormAdd, setObsFromForms, setObsIdToFormAdd } from '../../../store/reducers/formsAdditionalHelper';
import ReferralReviewButton from './referralReview/referralReviewButton';
import { getPatientFlagValue } from '../mainScreen/util/patientFlagsUtil';
import { consultantDictionary } from '../../../controls/agyle/clinician/clinitianUtils';
import ControlConsultantComponent from '../../../controls/agyle/clinician/controlConsultantComponent';
import { findArrivedSlotAndUpdateToBeingSeen } from '../../../store/reducers/appointments';
import MainScreenDischargeButton from '../mainScreen/components/buttons/mainScreenDischargeButton';
import AcuityScoreControls from '../../../components/forms/acuityScore/acuityScoreControls';
import InitialAssessmentSummary from './initialAssessment/initialAssessmentSummary';
import { isMTSVersion } from '../../../components/forms/acuityScore/acuityScoreUtils';

const useStyles = makeStyles((theme) => ({
    contairner: {
        paddingLeft: 10,
        width: '100%',
    },
    button: {
        minWidth: 120,
    },
    divider: {
        width: '100%',
        margin: '20px 20px 10px 20px',
    },
    fullWidth: {
        width: '100%',
    },

    paper: {
        background: theme.palette.background.paper,
        margin: '10px 10px 0px 30px',
        padding: 10,
        width: '100%',
        display: 'grid',
        gridGap: 10,
        '&.paddingTop': {
            margin: '25px 10px 0px 30px',
        },
    },
    error: {
        color: '#f44336',
    },
    loading: {
        textAlign: 'center',
        fontWeight: 'bold',
    },
    infoObs: {
        color: '#434343',
        fontSize: 'medium',
        textAlign: 'center',
        fontWeight: 'bold',
        background: '#ffd966',
        marginBottom: 10,
        marginTop: 10,
        padding: 5,
        borderRadius: 5,
        marginLeft: 20,
    },
    speedDial: {
        position: 'absolute',
        left: -81,
        bottom: 130,
        transform: 'rotate(-90deg)',
        '-webkit-transform': 'rotate(-90deg)',
    },
    menuItems: {
        margin: 'auto',
        width: 'auto',
        padding: 5,
    },
    menuItem: {
        margin: 5,
        padding: 5,
    },
    accordion: {
        backgroundColor: theme.palette.accordion.backgroundColor,
    },
    seeAndTreatButton: {
        width: '100%',
        marginBottom: 10,
        fontSize: 14,
    },
}));

const SeeAndTreat = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);
    const dispatch = useDispatch();

    const dictChiefComplaint = useSelector(chiefComplaint);
    const version = dispatch(getEcdsVersion(props.patient?.admission?.startDate));
    const obsIdToSave = useSelector(obsIdToFormAdd);

    // Acuity find
    const referralData = useSelector(ctxReferralFormData);
    const ecdsAcuityDict = useSelector(acuity);

    const controller = new AbortController();
    const elementRef = useRef(null);

    const isFormDisabled = props.disableSave || props.patient?.admission?.status === 'DISCHARGE_REQUESTED';
    const [isDark, setIsDark] = useState(null);

    const [loadingForm, setLoadingForm] = useState(true);

    const [obs, setObs] = useState(null);
    const [obsLast, setObsLast] = useState(null);

    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const [newerObs, setNewerObs] = useState({
        status: false,
        date: null,
    });
    const [obsLoading, setObsLoading] = useState(false);
    const [isOpenObsDialog, openObsDialog, closeObsDialog] = useOpenState(false);

    const [openConfirmBox, setOpenConfirmBox] = useState(false);
    const option = useRef(null);
    const preventScroll = useRef(false);

    const [error, setError] = useState(false);
    const [errorPicker, setErrorPicker] = useState(null);

    // MAMS menu
    const [anchorMenu, setAnchorMenu] = useState(null);
    const openMenu = Boolean(anchorMenu);
    const [activeItems, setActiveItems] = useState({
        pastMedicalHistory: false,
        medications: false,
        socialHistory: false,
        allergiesAndReactions: false,
    });
    const [mams, setMams] = useState([]);

    const [activeStep, setActiveStep] = useState(0);
    const [formDisableDischarge, setFormDisableDischarge] = useState(false);

    // Form Data
    const [seeAndTreatForm, setSeeAndTreatForm] = useState({
        clinician: null,
        presentation: null,
        discriminator: null,
        dependency: null,
        when: moment().toISOString(),
        chiefComplaint: null,
        historyOfPresentingComplaint: null,
        obsId: null,
        obsNotRequired: null,
        obsNotRequiredTimestamp: null,
        painScore: 0,
        painScoreId: '',
        analgesiaGiven: false,
        analgesiaRefused: false,
        analgesiaDetails: '',
        examination: '',
        workingDiagnosis: '',

        diagnosis: null,
        treatment: null,
        investigation: null,
        safeguarding: null,

        pastMedicalHistory: null,
        allergiesAndReactions: null,
        medications: null,
        socialHistory: null,

        status: SEE_AND_TREAT_STATUSES.new,
        canDischargeFromForm: true,
        timeOfDocumentation: moment().toISOString(),
        timeOfOpenForm: moment().toISOString(),
    });

    const [triageAnalgesia, setTriageAnalgesia] = useState({
        given: false,
        details: '',
    });

    // Other forms state
    const [dischargeForm, setDischargeForm] = useState(null);
    const [chosenObs, setChosenObs] = useState([]);
    const [oneByOne, setOneByOne] = useStickyState(false, 'agyle_obsSideBySide');
    const [isOneByOne, setIsOneByOne] = useState(false);

    const [isRemoveTimestampOpen, openRemoveTimestamp, closeRemoveTimestamp] = useOpenState(false, true);

    // Uniq id for CoreDS
    const [historicPresentingComplaintId] = useState(() => uuidv4() + '');
    const [pastMedicalHistoryId] = useState(() => uuidv4() + '');
    const [medicationsId] = useState(() => uuidv4() + '');
    const [socialHistoryId] = useState(() => uuidv4() + '');
    const [allergiesAndReactionsId] = useState(() => uuidv4() + '');
    const [diagnosisInformationId] = useState(() => uuidv4() + '');

    const [fullFormMode, setFullFormMode] = useState(!props.isActionMode);

    const stepper = [
        createStepObject('Documentation', false, error),
        createStepObject('Assessments', false, false),
        createStepObject('Patient Discharge', false, false),
    ];

    // Configs
    const safeguardingLevelMandatory = getConfig('safeguardingLevelMandatory', false);
    const showDifferentialDiagnosisSeeAndTreat = getConfig('showDifferentialDiagnosisSeeAndTreat', false);
    const safeguardingDischargeMandatoryAdult = getConfig('safeguardingDischargeMandatoryAdult', false);
    const safeguardingDischargeMandatoryPaed = getConfig('safeguardingDischargeMandatoryPaed', false);
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');
    const copyObsButtonsAvailable = getConfig('copyObsButtonsAvailable', false);
    const referralReviewForm = getConfig('referralReviewForm', false);
    const schedulerModuleEnabled = getConfig('modules.scheduler.enabled', false);
    const seeAndTreatManchesterTriageMandatory = getConfig('seeAndTreatManchesterTriageMandatory', true);
    const fillTriage = getConfig('seeAndTreatForm.fillTriage', true);
    const updateCurrentClinician = getConfig('seeAndTreatForm.updateCurrentClinician', true);

    // Others
    const hasSRFlag = getPatientFlagValue(props.patient, 'S&R');
    const [isSmall, setIsSmall] = useState(false);

    useEffect(() => {
        setIsSmall(props._containerWidth < 730);
    }, [props._containerWidth]);

    // Speed dial items
    const actions = [
        {
            id: 'pastMedicalHistory',
            name: 'Medical History',
            component: (
                <CoreDataTextField
                    path="core.patient.pastMedicalHistory"
                    label={t('Past Medical History')}
                    variant="outlined"
                    showCoreValue
                    agyleRefresh
                    multiline
                    hideHistory={props.pastAttendances}
                    size="large"
                    noBorder={false}
                    withNewBackground
                />
            ),
        },
        {
            id: 'allergiesAndReactions',
            name: 'Allergies',
            component: (
                <CoreDataTextField
                    agyleRefresh
                    path="core.patient.allergies"
                    label={t('Allergies and reactions')}
                    variant="outlined"
                    showCoreValue
                    multiline
                    hideHistory={props.pastAttendances}
                    size="large"
                    noBorder={false}
                    withNewBackground
                />
            ),
        },
        {
            id: 'medications',
            name: 'Medications',
            component: (
                <CoreDataTextField
                    agyleRefresh
                    path="core.patient.medications"
                    label={t('Medications')}
                    variant="outlined"
                    showCoreValue
                    multiline
                    hideHistory={props.pastAttendances}
                    size="large"
                    noBorder={false}
                    withNewBackground
                />
            ),
        },
        {
            id: 'socialHistory',
            name: 'Social History',
            component: (
                <CoreDataTextField
                    agyleRefresh
                    path="core.patient.socialHistory"
                    label={t('Social History')}
                    variant="outlined"
                    showCoreValue
                    multiline
                    hideHistory={props.pastAttendances}
                    size="large"
                    noBorder={false}
                    withNewBackground
                />
            ),
        },
    ];

    // Add buttons
    useEffect(() => {
        if (props.onHideSubmit) props.onHideSubmit(true);
        const buttons = [];

        if (props.patient?.studies?.seeAndTreat?.status === 'timestamp') {
            buttons.push(
                <AgyleButton variant="contained" onClick={openRemoveTimestamp}>
                    {t('Remove Timestamp')}
                </AgyleButton>,
            );
        }

        if (!fullFormMode) {
            buttons.push(
                <AgyleButton onClick={handleOpenFullFormTimestamp} variant="contained" color="primary">
                    {t('Open Form')}
                </AgyleButton>,
            );
        }

        if (referralReviewForm && fullFormMode && hasSRFlag) {
            buttons.push(<ReferralReviewButton small shortText={isSmall} />);
        }

        if (!props.disableSave) {
            if (fullFormMode) {
                buttons.push(
                    <AgyleButton variant="outlined" onClick={handleBack} disabled={activeStep < 1} colour="primary">
                        {t('Back')}
                    </AgyleButton>,
                );
            }

            buttons.push(
                <AgyleButton
                    onClick={handleOpenMessageBox.bind(this, !fullFormMode ? 'timestamp' : 'draft')}
                    variant="contained"
                    color="primary"
                    disabled={checkIfPatientIsDischarged()}
                >
                    {t(!fullFormMode ? 'Submit' : 'Save Draft')}
                </AgyleButton>,
            );

            if (activeStepCheck(true) && fullFormMode) {
                buttons.push(
                    <AgyleButton onClick={handleNext} disabled={activeStep > 2 || superFunkcja()} variant="contained">
                        {t('Next')}
                    </AgyleButton>,
                );
            }

            if (activeStepCheck(false)) {
                if (seeAndTreatForm.canDischargeFromForm) {
                    buttons.push(
                        <MainScreenDischargeButton
                            key="discharge-button-with-templates"
                            button={dischargeButtonConfig}
                            seeAndTreat={seeAndTreatForm}
                        />,
                        <AgyleButton
                            onClick={handleOpenMessageBox.bind(this, 'discharge')}
                            variant="contained"
                            color="primary"
                            disabled={checkIfPatientIsDischarged() || formDisableDischarge}
                        >
                            {t('Discharge')}
                        </AgyleButton>,
                    );
                } else {
                    buttons.push(
                        <AgyleButton
                            onClick={handleOpenMessageBox.bind(this, 'onlySave')}
                            variant="contained"
                            color="primary"
                            disabled={checkIfPatientIsDischarged()}
                            startIcon={<AgyleSubmitIcon />}
                        >
                            {t('Submit')}
                        </AgyleButton>,
                    );
                }
            }
        }
        if (props.agyleDialogInsertButtons) props.agyleDialogInsertButtons(buttons);
        if (props.onCustomButtons) props.onCustomButtons(buttons);
    }, [
        props?.patient,
        fullFormMode,
        activeStep,
        seeAndTreatForm,
        props.disableSave,
        dischargeForm,
        errorPicker,
        formDisableDischarge,
        isSmall,
    ]);

    useEffect(() => {
        setIsOneByOne(oneByOne);
    }, []);

    // Init
    useEffect(() => {
        let initData = {
            chiefComplaint: getChiefOfComplaint(),
        };

        // Prepopulate data from Triage / Clerking
        const clerking = props.patient?.clerking ? _.cloneDeep(props.patient.clerking) : null;
        const triage = props.patient?.triage ? _.cloneDeep(props.patient.triage) : null;
        const seeAndTreatSaved = props.patient?.studies?.seeAndTreat ? _.cloneDeep(props.patient.studies?.seeAndTreat) : null;
        const initialAssessment = props.patient?.initialAssessment ? _.cloneDeep(props.patient.initialAssessment) : null;

        // Prepopulate data from diagnosis form Main Screen
        if (props.patient?.diagnosis?.primary) {
            initData = {
                ...initData,
                diagnosis: props.patient?.diagnosis?.primary || null,
            };
        }

        // Prepopulate data from triage if form was not saved
        if (triage?.triageClinicians && !seeAndTreatSaved) {
            initData = {
                ...initData,
                clinician: triage?.triageClinicians || null,
                when: triage?.when || moment().toISOString(),
                presentation: triage?.presentation || null,
                discriminator: triage?.discriminator || null,
                dependency: triage?.dependency || initData?.dependency,
                chiefComplaint: triage?.chiefComplaint || initData?.chiefComplaint,
                historyOfPresentingComplaint: triage?.historicPresentingComplaint || null,
                examination: triage?.examination || '',
                treatment: triage?.treatmentAndManagementPlan || null,
                investigation: triage?.investigations || null,

                pastMedicalHistory: triage?.pastMedicalHistory || null,
                allergiesAndReactions: triage?.allergies || null,
                medications: triage?.medications || null,
                socialHistory: triage?.socialHistory || null,
            };
        }

        if (initialAssessment && !triage?.triageClinicians && !seeAndTreatSaved) {
            initData = {
                ...initData,
                clinician: initialAssessment?.clinician || null,
                when: initialAssessment?.when || moment().toISOString(),
                presentation: initialAssessment?.presentation || null,
                discriminator: initialAssessment?.discriminator || null,
            };
        }

        if (referralData && !initData?.discriminator && !seeAndTreatSaved) {
            const isMTS = isMTSVersion(null, props.patient?.admission?.startDate);
            if (!isMTS) {
                const acuity = _.find(
                    ecdsAcuityDict,
                    (ref) => ref.ecdsVersion === version && _.startsWith(ref?.__description, referralData?.ecdsAcuityScore),
                );
                if (acuity) {
                    initData = {
                        ...initData,
                        discriminator: acuity || null,
                    };
                }
            }
        }

        // Prepopulate data from clerking and override triage data if form was not saved
        if (clerking && !seeAndTreatSaved) {
            initData = {
                ...initData,
                chiefComplaint: clerking?.chiefComplaint || initData?.chiefComplaint,
                historyOfPresentingComplaint: clerking?.historicPresentingComplaint || null,
                examination: clerking?.examination || '',

                pastMedicalHistory: triage?.pastMedicalHistory || initData.pastMedicalHistory,
                allergiesAndReactions: triage?.allergies || initData.allergiesAndReactions,
                medications: triage?.medications || initData.medications,
                socialHistory: triage?.socialHistory || initData.socialHistory,
            };

            if (showDifferentialDiagnosisSeeAndTreat) {
                initData = {
                    ...initData,
                    workingDiagnosis: clerking?.workingDiagnosis || '',
                };
            }
        }

        // Override all prepopulate data if form was saved before
        if (seeAndTreatSaved) {
            initData = {
                ...seeAndTreatSaved,
            };
        }

        if (!initData.clinician && props.patient?.currentClinician) {
            initData = {
                ...initData,
                clinician: props.patient?.currentClinician?.__description ? props.patient?.currentClinician?.__description : null,
            };
        }

        if (!initData.clinician && !initData.when && props.patient?.firstClinician) {
            initData = {
                ...initData,
                clinician: props.patient?.firstClinician?.who ? props.patient?.firstClinician?.who : null,
                when: props.patient?.firstClinician?.when ? props.patient?.firstClinician?.when : null,
            };
        }

        if (!initData.historyOfPresentingComplaint && props.patient?.metadata?.historyOfPresentingComplaint) {
            initData = {
                ...initData,
                historyOfPresentingComplaint: props.patient?.metadata?.historyOfPresentingComplaint,
            };
        }

        if (initData.obsId) {
            // Load saved obs
            loadLastObs(initData.obsId);
        } else {
            // Load last obs
            loadLastObs();
        }

        if (safeguardingLevelMandatory) {
            initData = {
                ...initData,
                canDischargeFromForm: getSafeguardingLevel(props.patient) === 'green',
            };
        }

        // Load Analgesia from triage
        setTriageAnalgesia({
            given: triage?.analgesia || false,
            details: triage?.analgesiaDetails || '',
        });

        setSeeAndTreatForm((prevState) => ({
            ...prevState,
            ...initData,
        }));

        // Prepopulate MAMS
        if (initData.pastMedicalHistory || initData.allergiesAndReactions || initData.medications || initData.socialHistory) {
            preventScroll.current = true;
            const mamsLoad = {
                pastMedicalHistory: Boolean(initData.pastMedicalHistory),
                allergiesAndReactions: Boolean(initData.allergiesAndReactions),
                medications: Boolean(initData.medications),
                socialHistory: Boolean(initData.socialHistory),
            };
            const mamsElements = [];

            _.forEach(mamsLoad, (value, key) => {
                if (value) {
                    const component = _.find(actions, { id: key });
                    if (component)
                        mamsElements.push({
                            id: key,
                            component: React.cloneElement(component.component, {
                                disabled: isFormDisabled,
                                patient: props.patient.patient.__id,
                                value: initData[key],
                                instanceId: getInstance(key),
                                onChange: (event) => handleChangeMAMS(event, key),
                            }),
                        });
                }
            });

            setMams(mamsElements);
            setActiveItems({ ...mamsLoad });
        }
        setLoadingForm(false);
        return () => {
            controller.abort();
        };
    }, []);

    let type = obsTypes.pews;
    if (props.patientAgeType !== PATIENT_AGE_TYPE.PAEDS) type = obsTypes.news;

    useEffect(() => {
        refreshObservation(type);
    }, []);

    // Get last observation from API
    function refreshObservation(newType) {
        const currentPatientInWard = props?.patient;
        API.getFullObservations(currentPatientInWard, newType, false, controller.signal)
            .then((result) => {
                let obs = [];
                _.forEach(result, (apiObs) => {
                    obs.push(mapObservationFromAPI(apiObs));
                });
                setObsLast(obs);
            })
            .catch((error) => {
                console.error('Observations.API.getObservations: ', error);
            });
    }

    // Scroll to bottom after add MAMS object
    useEffect(() => {
        if (!_.isEmpty(mams) && elementRef && elementRef.current && !preventScroll.current) {
            elementRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [mams, elementRef.current]);

    useEffect(() => {
        if (window._getTheme() === 'dark') setIsDark(true);
        else setIsDark(false);
    }, []);

    useEffect(() => {
        if (obsIdToSave) {
            setSeeAndTreatForm((prevState) => ({ ...prevState, obsId: obsIdToSave }));
            loadLastObs(obsIdToSave);
            dispatch(setObsIdToFormAdd(null));
        }
    }, [obsIdToSave]);

    const handleOpenFullFormTimestamp = () => {
        if (props.handleOpenFullFormTimestamp) props.handleOpenFullFormTimestamp();
    };

    const loadLastObs = (id) => {
        setObsLoading(true);
        API.getFullObservations(props.patient, props.patientAgeType === PATIENT_AGE_TYPE.PAEDS ? 'pews' : 'news', true, controller.signal)
            .then((result) => {
                const savedId = id || seeAndTreatForm.obsId;

                const savedObs = _.filter(result, { __id: savedId });
                let filteredObs = _.orderBy(result, ['examinationStart'], ['desc']);
                let lastObs;

                if (!_.isEmpty(filteredObs)) {
                    filteredObs = _.filter(filteredObs, (element) => {
                        return (
                            (element.status === 'completed' || element.status === 'notRecorded') &&
                            (element.investigation === 'pews' || element.investigation === 'news')
                        );
                    });

                    lastObs = !_.isEmpty(filteredObs) ? mapObservationFromAPI(filteredObs[0]) : null;
                }

                let lastObsData = {
                    date: lastObs ? lastObs.startedDate : null,
                };

                if (!_.isEmpty(savedObs)) {
                    setObs(mapObservationFromAPI(savedObs[0]));
                    lastObsData = {
                        ...lastObsData,
                        status: lastObs.startedDate > savedObs.startedDate,
                    };
                    setNewerObs(lastObsData);
                } else {
                    lastObsData = { ...lastObsData, status: !_.isEmpty(filteredObs) };
                    setNewerObs(lastObsData);
                }
            })
            .finally(() => {
                setObsLoading(false);
            });
    };

    const getChiefOfComplaint = () => {
        let chief = null;
        const description = props.patient?.admission?.initialAEComplaintDescription;
        const code = props.patient?.admission?.initialAEComplaintSnomedCode;

        _.forEach(dictChiefComplaint, (el) => {
            if (
                el.ecdsVersion === version &&
                ((code && _.startsWith(el?.snomedCode, code)) || _.startsWith(el?.__description, description))
            ) {
                chief = el;
            }
        });
        return chief;
    };

    const prepareNote = (dateOfForm) => {
        const clone = _.cloneDeep(dateOfForm);
        if (clone) {
            return Notes.getSeeAndTreatNote(clone);
        }
    };

    const prepareTimeStampNote = (dateOfForm) => {
        const clone = _.cloneDeep(dateOfForm);
        if (clone) {
            return Notes.getActionSATNote(clone);
        }
    };

    const saveDraft = () => {
        if (errorPicker) {
            dispatch(setSnackbar({ type: 'warning', message: 'Wrong date.' }));
        } else {
            setOpenConfirmBox(false);

            let data = {
                ...seeAndTreatForm,
                status: SEE_AND_TREAT_STATUSES.draft,

                by: tester,
                byLogin: userObject?.userName,
                formSaved: moment().toISOString(),

                firstBy: seeAndTreatForm?.firstBy ? seeAndTreatForm?.firstBy : tester,
                firstByLogin: seeAndTreatForm?.firstByLogin ? seeAndTreatForm?.firstByLogin : userObject?.userName,
                firstFormSaved: seeAndTreatForm?.firstFormSaved ? seeAndTreatForm?.firstFormSaved : moment().toISOString(),
            };

            const note = prepareNote(data);

            delete data.timeOfOpenForm;

            const objToSave = [];

            const sat = {
                name: 'studies.seeAndTreat',
                value: data,
                noteToAdd: note,
            };

            addOtherForms(objToSave, true, sat);

            if (!props.patient?.studies?.seeAndTreat && schedulerModuleEnabled) {
                dispatch(findArrivedSlotAndUpdateToBeingSeen(props.patient?.__id, props.patient?.patient?.hospitalNumber));
            }
            if (props.onSave) props.onSave(objToSave);
            if (props._customOnSave) props._customOnSave(objToSave);
            props.onClose();
        }
    };

    const validTimestamp = () => {
        const isValid = !_.isNil(seeAndTreatForm.clinician) && seeAndTreatForm.when && moment(seeAndTreatForm.when).isValid();
        setError(!isValid);
        return isValid;
    };

    const saveTimestamp = () => {
        setOpenConfirmBox(false);
        if (validTimestamp()) {
            let data = {
                ...seeAndTreatForm,
                status: SEE_AND_TREAT_STATUSES.timestamp,

                by: tester,
                byLogin: userObject?.userName,
                formSaved: moment().toISOString(),

                firstBy: seeAndTreatForm?.firstBy ? seeAndTreatForm?.firstBy : tester,
                firstByLogin: seeAndTreatForm?.firstByLogin ? seeAndTreatForm?.firstByLogin : userObject?.userName,
                firstFormSaved: seeAndTreatForm?.firstFormSaved ? seeAndTreatForm?.firstFormSaved : moment().toISOString(),
            };

            const note = prepareTimeStampNote(data);

            delete data.timeOfOpenForm;

            const objToSave = [];

            const sat = {
                name: 'studies.seeAndTreat',
                value: data,
                noteToAdd: note,
            };

            addOtherForms(objToSave, true, sat);

            if (!props.patient?.studies?.seeAndTreat && schedulerModuleEnabled) {
                dispatch(findArrivedSlotAndUpdateToBeingSeen(props.patient?.__id, props.patient?.patient?.hospitalNumber));
            }

            if (props.onSave) props.onSave(objToSave);
            if (props._customOnSave) props._customOnSave(objToSave);
            props.onClose();
        }
    };

    const isFormValid = () => {
        let check = true;
        let text = '';

        if (
            !seeAndTreatForm.clinician ||
            !seeAndTreatForm.when ||
            (seeAndTreatManchesterTriageMandatory && !seeAndTreatForm.discriminator) ||
            (seeAndTreatManchesterTriageMandatory && !seeAndTreatForm.presentation)
        ) {
            check = false;
            text = 'Not all mandatory fields in the first section of documentation tab are completed';
        } else if (!seeAndTreatForm.chiefComplaint) {
            check = false;
            text = 'Chief Complaint must be completed';
        } else if (!seeAndTreatForm.dependency) {
            check = false;
            text = 'Dependency must be completed';
        } else if (
            seeAndTreatForm.canDischargeFromForm &&
            !selectedTemplate?.diagnosisRequired &&
            (_.isEmpty(seeAndTreatForm.diagnosis) || _.isEmpty(seeAndTreatForm.investigation) || _.isEmpty(seeAndTreatForm.treatment))
        ) {
            check = false;
            text = 'At least one Diagnosis, Investigation and Treatment must be selected';
        } else if (
            seeAndTreatForm.canDischargeFromForm &&
            !selectedTemplate?.diagnosisRequired &&
            (_.isEmpty(seeAndTreatForm.investigation) || _.isEmpty(seeAndTreatForm.treatment))
        ) {
            check = false;
            text = 'At least one Investigation and Treatment must be selected';
        } else if (
            seeAndTreatForm.canDischargeFromForm &&
            !selectedTemplate?.safeguardingNotRequired &&
            _.isEmpty(seeAndTreatForm.safeguarding) &&
            ((props.patientAgeType !== PATIENT_AGE_TYPE.PAEDS && safeguardingDischargeMandatoryAdult) ||
                (props.patientAgeType === PATIENT_AGE_TYPE.PAEDS && safeguardingDischargeMandatoryPaed))
        ) {
            check = false;
            text = 'Safeguarding is mandatory for discharge';
        } else if (seeAndTreatForm.canDischargeFromForm && selectedTemplate?.safeguardingNotRequired) {
            check = true;
        } else if (_.isEmpty(dischargeForm) && seeAndTreatForm.canDischargeFromForm) {
            check = false;
            text = 'Discharge form must be completed';
        } else if (errorPicker) {
            check = false;
            text = 'Wrong date';
        }

        if (!check) {
            dispatch(setSnackbar({ type: 'warning', message: `${text}.` }));
            setError(true);
        } else {
            setError(false);
        }

        return check;
    };

    const discharge = (isDischarge) => {
        setOpenConfirmBox(false);
        if (isFormValid()) {
            let data = {
                ...seeAndTreatForm,
                status: SEE_AND_TREAT_STATUSES.completed,

                by: tester,
                byLogin: userObject?.userName,
                formSaved: moment().toISOString(),

                firstBy: seeAndTreatForm?.firstBy ? seeAndTreatForm?.firstBy : tester,
                firstByLogin: seeAndTreatForm?.firstByLogin ? seeAndTreatForm?.firstByLogin : userObject?.userName,
                firstFormSaved: seeAndTreatForm?.firstFormSaved ? seeAndTreatForm?.firstFormSaved : moment().toISOString(),
            };

            const note = prepareNote(data);

            delete data.timeOfOpenForm;

            const objToSave = [];

            const sat = {
                name: 'studies.seeAndTreat',
                value: data,
                noteToAdd: note,
            };

            addOtherForms(objToSave, false, sat, isDischarge);

            keyPerformanceIndicator(objToSave, props.patient, ['firstInitialAssessment', 'firstClinicianSaved'], {
                when: data.when,
                who: data.clinician,
            });

            if (isDischarge) {
                const dischargeValidation = dischargeForm[0]?.validation;

                if (dischargeValidation) {
                    dispatch(setSnackbar({ type: 'warning', message: `${dischargeValidation}.` }));
                    setError(true);
                } else {
                    if (props.discharge) props.discharge(objToSave);

                    if (props.onDischargePatient) {
                        props.onDischargePatient(objToSave);
                    }
                }
            } else {
                if (props.onSave) props.onSave(objToSave);
                if (props._customOnSave) props._customOnSave(objToSave);
                if (props.onClose) props.onClose();
            }
        }
    };

    const fillTriageObject = () => {
        const triage = triageBase();

        triage.who = tester;
        triage.whoUserName = userObject.userName;
        triage.when = seeAndTreatForm.when;
        triage.triageClinicians = seeAndTreatForm.clinician;

        triage.dependency = seeAndTreatForm.dependency;
        triage.chiefComplaint = seeAndTreatForm.chiefComplaint;
        triage.presentation = seeAndTreatForm.presentation;
        triage.discriminator = seeAndTreatForm.discriminator;
        triage.chiefComplaint = seeAndTreatForm.chiefComplaint;

        triage.analgesiaSAT = seeAndTreatForm.analgesiaGiven;
        triage.analgesiaSATDetails = seeAndTreatForm.analgesiaDetails;

        triage.historicPresentingComplaint = seeAndTreatForm.historyOfPresentingComplaint;
        triage.pastMedicalHistory = seeAndTreatForm.pastMedicalHistory;
        triage.allergies = seeAndTreatForm.allergiesAndReactions;
        triage.medications = seeAndTreatForm.medications;
        triage.socialHistory = seeAndTreatForm.socialHistory;

        triage.examination = seeAndTreatForm.examination;

        triage.treatmentAndManagementPlanTriage = seeAndTreatForm.treatment;
        triage.investigationsTriage = seeAndTreatForm.investigation;

        triage.observationNotRequired = seeAndTreatForm.obsNotRequired;
        triage.observationNotRequiredTimestamp = seeAndTreatForm.obsNotRequiredTimestamp;
        triage.savedObservationId = seeAndTreatForm.obsId;

        triage.painScore = seeAndTreatForm.painScore;
        triage.painScoreId = seeAndTreatForm.painScoreId;

        triage.firstWho = tester;
        triage.firstWhoUserName = userObject.userName;
        triage.firstWhen = seeAndTreatForm.when;
        triage.firstDiscriminator = seeAndTreatForm.discriminator;

        return triage;
    };

    const addOtherForms = (objToSave, isDraft, seeAndTreatObj, isDischarge = false) => {
        // Triage object
        let cloneTriage = _.cloneDeep(props.patient?.triage) || {};
        let triageNoteNeeded = false;

        if (!isDischarge) {
            if (!props.patient?.currentClinician && updateCurrentClinician) {
                objToSave.push({
                    name: 'currentClinician',
                    value: {
                        ...seeAndTreatForm?.clinician,
                        when: seeAndTreatForm?.when,
                        timestamp: moment().toISOString(),
                    },
                });
            }
            keyPerformanceIndicator(objToSave, props.patient, ['firstClinicianSaved'], {
                when: seeAndTreatForm?.when,
                who: seeAndTreatForm?.clinician,
            });
        }

        if ((!cloneTriage || !cloneTriage.who || !cloneTriage.discriminator) && !isDraft) {
            cloneTriage = fillTriageObject();
            triageNoteNeeded = true;
        }

        // Diagnosis, Treatments and Investigations
        if (!_.isEmpty(seeAndTreatForm.treatment)) {
            cloneTriage.treatmentAndManagementPlan = !_.isEmpty(seeAndTreatForm.treatment)
                ? _.unionBy(cloneTriage.treatmentAndManagementPlan, seeAndTreatForm.treatment, 'value.snomedCode')
                : cloneTriage.treatmentAndManagementPlan;
        }

        if (!_.isEmpty(seeAndTreatForm.investigation)) {
            cloneTriage.investigations = !_.isEmpty(seeAndTreatForm.investigation)
                ? _.unionBy(cloneTriage.investigations, seeAndTreatForm.investigation, 'value.snomedCode')
                : cloneTriage.investigations;
        }

        let triageObject = {
            name: 'triage',
            value: cloneTriage,
        };

        if (triageNoteNeeded) {
            triageObject = {
                ...triageObject,
                noteToAdd: Notes.getTriageNote(cloneTriage, getPatientTriageType(), true),
            };
        }

        if (!_.isEmpty(cloneTriage) && fillTriage) {
            objToSave.push(triageObject);
        }

        if (seeAndTreatObj) {
            objToSave.push(seeAndTreatObj);
        }

        // Discharge note will be on top
        if (dischargeForm && seeAndTreatForm.canDischargeFromForm) {
            const dischargeIndex = _.findIndex(dischargeForm, { name: 'studies.discharge' });
            if (dischargeIndex !== -1) {
                let discharge = dischargeForm[dischargeIndex];
                discharge.value = {
                    ...discharge.value,
                    status: isDraft ? 'DRAFT' : 'COMPLETED',
                };

                // if S&T is a draft then don't need to save discharge form clinician note
                if (isDraft) {
                    delete dischargeForm[dischargeIndex].note;
                    delete dischargeForm[dischargeIndex].noteType;
                }

                objToSave.push(...dischargeForm);
            }
        }

        if (!_.isEmpty(seeAndTreatForm.diagnosis)) {
            let clone = _.cloneDeep(props.patient?.diagnosis);
            if (!clone) {
                clone = diagnosisBase();
            }

            clone.primary = _.unionBy(clone.primary, seeAndTreatForm.diagnosis, 'value.snomedCode');
            objToSave.push({ name: 'diagnosis', value: clone });
        }
    };

    const handleOpenMessageBox = (type) => {
        setOpenConfirmBox(true);
        option.current = type;
    };

    const handleCloseMessageBox = () => {
        setOpenConfirmBox(false);
    };

    const handleOpenMenu = (event) => {
        setAnchorMenu(event.currentTarget);
    };

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleSavedObs = (type) => {
        dispatch(
            setSnackbar({
                type: 'success',
                message: type === 'obs' ? 'Observations have been successfully saved.' : 'Pain score have been successfully saved.',
            }),
        );
    };

    const handleDischargeOption = () => {
        setSeeAndTreatForm((prevState) => ({
            ...prevState,
            canDischargeFromForm: !prevState.canDischargeFromForm,
        }));
    };

    const copyLastObs = () => {
        // const lastObs = Formatter.formatObservationToText(props.patient?.patient?.lastObservation);
        const lastObs = obsLast && Formatter.formatObservationToText(obsLast[obsLast.length - 1]);

        setSeeAndTreatForm((prevState) => ({
            ...prevState,
            examination: prevState.examination !== '' ? prevState.examination + '\n' + lastObs : lastObs,
        }));
    };

    const handleChangeMAMS = (event, id) => {
        setSeeAndTreatForm((prevState) => ({
            ...prevState,
            [id]: event.target.value,
        }));
    };

    const getInstance = (id) => {
        // eslint-disable-next-line default-case
        switch (id) {
            case 'pastMedicalHistory':
                return pastMedicalHistoryId;
            case 'allergiesAndReactions':
                return allergiesAndReactionsId;
            case 'socialHistory':
                return socialHistoryId;
            case 'medications':
                return medicationsId;
        }
    };

    const handleMAMSMenu = (id) => {
        let clone = _.cloneDeep(mams);
        if (_.findIndex(clone, { id }) !== -1) {
            _.remove(clone, { id });
        } else {
            const component = _.find(actions, { id });
            if (component) {
                preventScroll.current = false;
                clone.push({
                    id,
                    component: React.cloneElement(component.component, {
                        disabled: isFormDisabled,
                        patient: props.patient.patient.__id,
                        value: seeAndTreatForm[id],
                        instanceId: getInstance(id),
                        onChange: (event) => handleChangeMAMS(event, id),
                    }),
                });
            }
        }
        setMams(clone);
        setAnchorMenu(null);
    };

    const handleChangePresentation = async (value) => {
        const getDiscriminator = await findDefaultDiscriminator(value);
        setSeeAndTreatForm((pr) => ({
            ...pr,
            presentation: value,
            discriminator: getDiscriminator,
        }));
    };

    // Set default discriminator when presentation is change
    const findDefaultDiscriminator = async (value) => {
        let item = null;
        if (!_.isNil(value)) {
            const getDictionaryURL = () => {
                let localFilter = "description regex '.*Other.*' ";
                localFilter += ' AND ';
                localFilter += `presentation eq '${value.__code}'`;
                return `/$dict/TriageDiscriminators?$filter=${encodeURIComponent(localFilter)}`;
            };

            await API.getDictByURL(getDictionaryURL()).then((find) => {
                if (!_.isEmpty(find.__data)) {
                    item = find.__data[0];
                }
            });
        }
        return item;
    };

    const handleRemoveTimestamp = () => {
        const data = [{ 'studies.seeAndTreat': null }];
        API.updatePatientInWard(props.patient.__id, data)
            .then((res) => {
                if (props.modifyPatient) props.modifyPatient([{ name: 'studies.seeAndTreat', value: null }]);
                dispatch(
                    addNoteForPatient(
                        props.patient.patient.__id,
                        {
                            note: Notes.getRemoveTimestampNote('seeAndTreat', false, false),
                            noteType: 'timestamp',
                            patientsInWardsRef: [props.patient.__id],
                            episodeId: props.patient?.admission?.consultantEpisodeId || null,
                        },
                        props.patient.__id,
                    ),
                );
                dispatch(setSnackbar({ type: 'success', message: 'Timestamp successful removed.' }));
            })
            .catch((err) => {
                dispatch(setSnackbar({ type: 'error', message: 'Error while removing see and treat timestamp.' }));
            })
            .finally(() => {
                closeRemoveTimestamp();
                if (props.onClose) props.onClose();
                if (props._onForceClose) props._onForceClose();
            });
    };

    const renderStep = () => {
        // eslint-disable-next-line default-case
        switch (activeStep) {
            case 0:
                return renderDocumentation();
            case 1:
                return renderAssessment();
            case 2:
                return renderDischarge();
        }
    };

    const renderClerkingClinician = () => {
        const handleDictionaryChangeValue = (name, value) => {
            handleChange({ target: { name, value: value } });
        };

        const handleChange = (event) => {
            const { value, name } = event.target;

            setSeeAndTreatForm((prevState) => ({
                ...prevState,
                [name]: value,
            }));
        };

        return (
            <>
                <Grid item xs={isSmall ? 12 : 6}>
                    <ControlConsultantComponent
                        error={error && !seeAndTreatForm.clinician}
                        agyleRefresh
                        label={t('Clerking Clinician')}
                        value={seeAndTreatForm.clinician}
                        disabled={isFormDisabled}
                        findUser={userObject?.userName}
                        allowedConsultants={consultantDictionary.seeAndTreat}
                        loading={loadingForm}
                        onChange={(value) => handleDictionaryChangeValue('clinician', value)}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleDatePicker
                        error={!seeAndTreatForm.when && (error || errorPicker)}
                        label={t('Date and Time of See and Treat')}
                        value={seeAndTreatForm.when ? moment(seeAndTreatForm.when) : null}
                        disableToolbar={true}
                        minDate={isFormDisabled ? null : moment(calculateArrivalDate(props.patient))}
                        maxDate={isFormDisabled ? null : moment()}
                        disabled={isFormDisabled}
                        onError={setErrorPicker}
                        onChange={(value) => {
                            if (value)
                                setSeeAndTreatForm((pr) => ({
                                    ...pr,
                                    when: value.toISOString(),
                                }));
                        }}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>
            </>
        );
    };

    const handleOpenObs = () => {
        dispatch(setObsFromForms(true));
        dispatch(openPatientForm('obs'));
    };

    const renderDocumentation = () => {
        const handleDictionaryChangeValue = (name, value) => {
            handleChange({ target: { name, value: value } });
        };

        const handleChange = (event) => {
            const { value, name } = event.target;

            setSeeAndTreatForm((prevState) => ({
                ...prevState,
                [name]: value,
            }));
        };

        return (
            <Grid container direction="row" spacing={2}>
                <Grid item xs={12}>
                    <ObservationTable patient={props.patient} />
                </Grid>

                <Grid item xs={12}>
                    <InitialAssessmentSummary patient={props.patient} />
                </Grid>

                {renderClerkingClinician()}

                <Grid item xs={12}>
                    <AcuityScoreControls
                        value={seeAndTreatForm}
                        onChange={setSeeAndTreatForm}
                        startDate={props.patient?.admission?.startDate}
                        canEdit={!isFormDisabled}
                        isSmall={isSmall}
                        isError={error}
                    />
                </Grid>

                <Divider className={classes.divider} />

                <Grid item xs={12}>
                    <ControlDependency
                        setDefault
                        agyleRefresh
                        label={t('Dependency')}
                        value={seeAndTreatForm.dependency}
                        disabled={isFormDisabled}
                        error={error && !seeAndTreatForm.dependency}
                        onChange={(value) => handleDictionaryChangeValue('dependency', value)}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Grid item xs={12}>
                    <ChiefOfComplaint
                        agyleRefresh
                        value={seeAndTreatForm.chiefComplaint}
                        label={t('Chief Complaint')}
                        disabled={isFormDisabled}
                        error={error && !seeAndTreatForm.chiefComplaint}
                        onChange={(value) => handleDictionaryChangeValue('chiefComplaint', value)}
                        admitDate={props.patient?.admission?.startDate}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Grid item xs={12}>
                    <CoreDataTextField
                        patient={props.patient.patient.__id}
                        path="core.patient.historicPresentingComplaint"
                        instanceId={historicPresentingComplaintId}
                        label={t('History of Presenting Complaint')}
                        variant="outlined"
                        agyleRefresh
                        value={seeAndTreatForm.historyOfPresentingComplaint}
                        onChange={(event) =>
                            setSeeAndTreatForm((prevState) => ({
                                ...prevState,
                                historyOfPresentingComplaint: event.target.value,
                            }))
                        }
                        showCoreValue
                        multiline
                        disabled={isFormDisabled}
                        hideHistory={props.pastAttendances}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Divider className={classes.divider} />

                {seeAndTreatForm.obsNotRequired && (
                    <Grid item xs={12} className={classes.infoObs}>
                        {t(
                            '"OBS not required" action was registered in this S&T. To be able to add observation uncheck "OBS not required"',
                        )}
                    </Grid>
                )}

                <Grid item xs={12}>
                    {obsLoading && (
                        <Grid item xs={12}>
                            <div className={classes.loading}>
                                <p>{t('Loading Observation data. Please wait.')}</p>
                                <CircularProgress />
                            </div>
                        </Grid>
                    )}

                    {!obsLoading && (
                        <>
                            {newerObs && newerObs.status && (
                                <Grid item xs={12}>
                                    <InformationBox type="info">
                                        {t('Last observations recorded at ')}
                                        {moment(newerObs.date).format(defaultDateTimeFormat)}
                                    </InformationBox>
                                </Grid>
                            )}

                            <Grid container direction="row" spacing={2}>
                                {!obs && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <AgyleButton
                                                width="100%"
                                                variant="contained"
                                                disabled={isFormDisabled || obsLoading || seeAndTreatForm.obsNotRequired}
                                                className={classes.fullWidth}
                                                onClick={handleOpenObs}
                                            >
                                                {t('Add observation')}
                                            </AgyleButton>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <AgyleButton
                                                width="100%"
                                                variant="contained"
                                                disabled={isFormDisabled || obsLoading}
                                                className={classes.fullWidth}
                                                onClick={() =>
                                                    setSeeAndTreatForm((prevState) => ({
                                                        ...prevState,
                                                        obsNotRequired: !prevState.obsNotRequired,
                                                        obsNotRequiredTimestamp: moment().toISOString(),
                                                    }))
                                                }
                                            >
                                                {t('Observation not required')}
                                            </AgyleButton>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </>
                    )}
                </Grid>

                {!!obs && (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="h6">{t('Observation completed during see and treat')}</Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <ObservationTable specificObs={obs} patient={props.patient} />
                        </Grid>
                    </>
                )}

                <Grid item xs={12} justifyContent="center">
                    <PainScore
                        patient={props.patient}
                        value={seeAndTreatForm.painScore}
                        obsId={seeAndTreatForm.painScoreId}
                        disabled={isFormDisabled}
                        onChange={(value) =>
                            setSeeAndTreatForm((prevState) => ({
                                ...prevState,
                                painScore: value,
                            }))
                        }
                        onSaveObs={(data) => {
                            if (data.success) {
                                setSeeAndTreatForm((prevState) => ({
                                    ...prevState,
                                    painScoreId: data.obsId,
                                }));
                                handleSavedObs('painScore');
                            }
                        }}
                    />
                </Grid>

                <Divider className={classes.divider} />

                {triageAnalgesia.given && (
                    <>
                        <Grid item xs={12}>
                            <Grid container direction="row" style={{ marginBottom: 10 }}>
                                <Grid item xs={12} sm={5}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                disabled
                                                checked={triageAnalgesia.given}
                                                inputProps={{ 'aria-label': 'primary checkbox' }}
                                            />
                                        }
                                        label="Analgesia given at triage"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={7}>
                                    <AgyleTextField
                                        className={classes.fullWidth}
                                        label={t('Triage Analgesia Details')}
                                        fullWidth
                                        multiline
                                        value={triageAnalgesia.details}
                                        variant="outlined"
                                        readOnly
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Divider className={classes.divider} />
                    </>
                )}

                <Grid item xs={12}>
                    <Grid container direction="row" style={{ marginBottom: 10 }}>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={(value) => {
                                            setSeeAndTreatForm((pr) => ({
                                                ...pr,
                                                analgesiaGiven: value.target.checked,
                                                analgesiaDetails: value.target.checked ? pr.analgesiaDetails : '',
                                            }));
                                        }}
                                        disabled={seeAndTreatForm.analgesiaRefused || isFormDisabled}
                                        checked={seeAndTreatForm.analgesiaGiven}
                                    />
                                }
                                label="Analgesia given at S&T"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={(value) => {
                                            setSeeAndTreatForm((pr) => ({
                                                ...pr,
                                                analgesiaRefused: value.target.checked,
                                                analgesiaDetails: !value.target.checked ? pr.analgesiaDetails : '',
                                            }));
                                        }}
                                        disabled={seeAndTreatForm.analgesiaGiven || isFormDisabled}
                                        checked={seeAndTreatForm.analgesiaRefused}
                                    />
                                }
                                label="Analgesia refused"
                            />
                        </Grid>
                    </Grid>
                </Grid>

                {seeAndTreatForm.analgesiaGiven && (
                    <Grid item xs={12}>
                        <TextFieldDebounce
                            label={t('Analgesia Details')}
                            disabled={isFormDisabled}
                            agyleRefresh
                            fullWidth
                            multiline
                            onChange={(event) => {
                                setSeeAndTreatForm((pr) => ({
                                    ...pr,
                                    analgesiaDetails: pr.analgesiaGiven ? event.target.value : '',
                                }));
                            }}
                            value={seeAndTreatForm.analgesiaDetails}
                            variant="outlined"
                            className={classes.fullWidth}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>
                )}

                <Divider className={classes.divider} />

                <Grid item xs={12}>
                    <TextFieldDebounce
                        disabled={isFormDisabled}
                        maxRows={25}
                        label={t('Examination')}
                        multiline
                        agyleRefresh
                        fullWidth
                        onChange={(event) => {
                            setSeeAndTreatForm((pr) => ({
                                ...pr,
                                examination: event.target.value,
                            }));
                        }}
                        value={seeAndTreatForm.examination}
                        variant="outlined"
                        changeValueTrigger
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />

                    <Grid item xs={12} style={{ display: 'flex', gap: '10px' }} sx={{ mb: 2, mt: 1 }}>
                        {copyObsButtonsAvailable && (
                            <AgyleButton
                                onClick={copyLastObs}
                                disabled={!props.patient?.patient?.lastObservation || isFormDisabled}
                                style={{ marginTop: 10 }}
                                variant="contained"
                            >
                                {t('Copy Last Observation')}
                            </AgyleButton>
                        )}

                        {copyObsButtonsAvailable && (
                            <AgyleButton
                                onClick={openObsDialog}
                                disabled={!props.patient?.patient?.lastObservation || isFormDisabled}
                                style={{ marginTop: 10 }}
                                variant="contained"
                            >
                                {t('Copy From Observation Table')}
                            </AgyleButton>
                        )}
                    </Grid>

                    {showDifferentialDiagnosisSeeAndTreat && (
                        <Grid item xs={12} style={{ marginTop: 10 }}>
                            <CoreDataTextField
                                agyleRefresh
                                fullWidth
                                patient={props.patient.patient.__id}
                                path="core.patient.workingDiagnosis"
                                instanceId={diagnosisInformationId}
                                label={t('Differential Diagnosis / Problem List')}
                                variant="outlined"
                                multiline
                                disabled={isFormDisabled}
                                value={seeAndTreatForm.workingDiagnosis}
                                onChange={(event) => {
                                    setSeeAndTreatForm((pr) => ({
                                        ...pr,
                                        workingDiagnosis: event.target.value,
                                    }));
                                }}
                                showCoreValue
                                height={70}
                                hideHistory={props.pastAttendances}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>
                    )}
                </Grid>

                <Divider className={classes.divider} />

                <Grid item xs={12}>
                    <ControlDiagnosis
                        disabled={isFormDisabled}
                        disableEditTable={isFormDisabled}
                        label={t('S&T Diagnosis')}
                        agyleRefresh
                        value={seeAndTreatForm.diagnosis}
                        onChange={(value) =>
                            setSeeAndTreatForm((prevState) => ({
                                ...prevState,
                                diagnosis: value,
                            }))
                        }
                        error={error && _.isEmpty(seeAndTreatForm.diagnosis)}
                        admitDate={props.patient?.admission?.startDate}
                        withBorder
                    />
                </Grid>

                <Grid item xs={12}>
                    <ControlInvestigations
                        disabled={isFormDisabled}
                        agyleRefresh
                        disableEditTable={isFormDisabled}
                        label={t('S&T Investigations')}
                        value={seeAndTreatForm.investigation}
                        onChange={(value) =>
                            setSeeAndTreatForm((prevState) => ({
                                ...prevState,
                                investigation: value,
                            }))
                        }
                        error={error && _.isEmpty(seeAndTreatForm.investigation)}
                        showIndicatedFlag="forSAT"
                        showAll
                        disableCloseOnSelect
                        admitDate={props.patient?.admission?.startDate}
                        withBorder
                    />
                </Grid>

                <Grid item xs={12}>
                    <ControlTreatment
                        disabled={isFormDisabled}
                        agyleRefresh
                        disableEditTable={isFormDisabled}
                        label={t('S&T Treatment')}
                        value={seeAndTreatForm.treatment}
                        onChange={(value) =>
                            setSeeAndTreatForm((prevState) => ({
                                ...prevState,
                                treatment: value,
                            }))
                        }
                        error={error && _.isEmpty(seeAndTreatForm.treatment)}
                        showIndicatedFlag="forSAT"
                        showAll
                        disableCloseOnSelect
                        admitDate={props.patient?.admission?.startDate}
                        withBorder
                    />
                </Grid>

                <Grid item xs={12}>
                    <Safeguarding
                        disabled={isFormDisabled}
                        label={t('S&T Safeguarding')}
                        agyleRefresh
                        paperBackground
                        bigLabel
                        clearIcon={null}
                        popupIcon={null}
                        showIndicatedFlag="forSAT"
                        showAll
                        value={seeAndTreatForm?.safeguarding}
                        onChange={(value) =>
                            setSeeAndTreatForm((prevState) => ({
                                ...prevState,
                                safeguarding: value,
                            }))
                        }
                        error={error && _.isEmpty(seeAndTreatForm.safeguarding)}
                        admitDate={props.patient?.admission?.startDate}
                        withBorder
                    />
                </Grid>

                {!_.isEmpty(mams) && (
                    <>
                        <Divider className={classes.divider} />

                        <Grid item xs={12} sx={{ ml: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {mams.map((element) => {
                                if (element.component) {
                                    return <Fragment key={element.id}>{element.component}</Fragment>;
                                }
                                return null;
                            })}
                        </Grid>
                    </>
                )}

                <Button size="small" className={classes.speedDial} variant="contained" onClick={handleOpenMenu} disabled={isFormDisabled}>
                    {t('Additional Information')}
                </Button>

                <Popover
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    sx={{ ml: 1 }}
                    open={openMenu}
                    anchorEl={anchorMenu}
                    onClose={() => setAnchorMenu(null)}
                >
                    <Grid container direction="row" spacing={2} className={classes.menuItems} justifyContent="center" alignItems="center">
                        {actions.map((element) => (
                            <AgyleButton
                                disabled={activeItems[element.id]}
                                className={classes.menuItem}
                                variant="contained"
                                key={element.name}
                                size="small"
                                onClick={handleMAMSMenu.bind(this, element.id)}
                            >
                                {element.name}
                            </AgyleButton>
                        ))}
                    </Grid>
                </Popover>
            </Grid>
        );
    };

    const renderAssessment = () => {
        return (
            <Grid sx={{ mt: 1 }} container direction="row" spacing={2}>
                {((seeAndTreatForm.chiefComplaint && seeAndTreatForm.chiefComplaint?.flagInjury === '1') ||
                    props.patientAgeType === PATIENT_AGE_TYPE.PAEDS) && (
                    <Grid item xs={12}>
                        <Grid item xs={12}>
                            <AgyleButton
                                width="100%"
                                variant="contained"
                                onClick={() => dispatch(openPatientForm('injury'))}
                                startIcon={<AgyleAssessmentIcon />}
                            >
                                {t('Open CWILTED')}
                            </AgyleButton>
                        </Grid>
                    </Grid>
                )}

                {props.patientAgeType !== PATIENT_AGE_TYPE.PAEDS && (
                    <>
                        <Grid item xs={12}>
                            <AgyleButton
                                width="100%"
                                variant="contained"
                                onClick={() => dispatch(openPatientForm('clinicalFrailtyScore'))}
                                startIcon={<AgyleAssessmentIcon />}
                            >
                                {t('Open Clinical Frailty Score')}
                            </AgyleButton>
                        </Grid>

                        <Grid item xs={12}>
                            <AgyleButton
                                width="100%"
                                variant="contained"
                                onClick={() => dispatch(openPatientForm('delirium'))}
                                startIcon={<AgyleAssessmentIcon />}
                            >
                                {t('Open Delirium Screening')}
                            </AgyleButton>
                        </Grid>
                    </>
                )}

                {props.patientAgeType === PATIENT_AGE_TYPE.PAEDS && (
                    <>
                        <Grid item xs={12}>
                            <AgyleButton
                                width="100%"
                                variant="contained"
                                onClick={() => dispatch(openPatientForm('household'))}
                                startIcon={<AgyleAssessmentIcon />}
                            >
                                {t('Open Household')}
                            </AgyleButton>
                        </Grid>

                        <Grid item xs={12}>
                            <AgyleButton
                                width="100%"
                                variant="contained"
                                onClick={() => dispatch(openPatientForm('paedSafeguarding'))}
                                startIcon={<AgyleAssessmentIcon />}
                                color={superFunkcja() && seeAndTreatForm.canDischargeFromForm ? 'warning' : 'primary'}
                            >
                                {t('Open Paediatric Safeguarding')}
                            </AgyleButton>
                        </Grid>
                    </>
                )}
            </Grid>
        );
    };

    const saveSafeFormStatus = (value) => {
        if (props.onSave) {
            props.onSave(value);
        }
    };

    const renderDischarge = () => {
        return (
            <Grid sx={{ mt: 1 }} container direction="row" spacing={2}>
                <Grid item xs={12}>
                    <DischargeForm
                        value={_.isEmpty(dischargeForm) ? null : dischargeForm[0]?.value}
                        patient={props.patient}
                        hideDiagTreatInv
                        onChange={setDischargeForm}
                        disabled={isFormDisabled}
                        isError={error}
                        populateDestination
                        seeAndTreat
                        closeDischarge={handleBack}
                        saveSafeFormStatus={saveSafeFormStatus}
                        isLocked={props.patient?.patient?.tags?.includes(PATIENT_TAGS.PATIENT_LOCKED)}
                        setFormDisableDischarge={setFormDisableDischarge}
                    />
                </Grid>
            </Grid>
        );
    };

    const checkIfPatientIsDischarged = () => {
        return (
            isFormDisabled ||
            (props.patient?.studies?.discharge && props.patient?.studies?.discharge?.status === 'completed') ||
            props.patient.admission?.status === 'DISCHARGE_REQUESTED'
        );
    };

    const dischargeButtonConfig = {
        name: 'discharge',
        display: t('Discharge'),
        color: 'primary',
        buttonVariant: 'contained',
        disabled: checkIfPatientIsDischarged() || formDisableDischarge,
        isReadyToTransfer: false,
        icon: null,

        event: (template) => {
            setDischargeForm(null);
            dispatch(setDischargeTemplate(template));
            setSelectedTemplate(template);
        },
    };

    const checkMessageBoxOption = () => {
        // eslint-disable-next-line default-case
        switch (option.current) {
            case 'draft': {
                saveDraft();
                break;
            }
            case 'timestamp': {
                saveTimestamp();
                break;
            }
            case 'discharge': {
                discharge(true);
                break;
            }

            case 'onlySave': {
                discharge(false);
                break;
            }
        }
    };

    const checkMessageBoxMessage = () => {
        let text = 'Are you sure you want to ';
        // eslint-disable-next-line default-case
        switch (option.current) {
            case 'draft': {
                text += 'save draft?';
                break;
            }
            case 'timestamp': {
                text += 'save timestamp?';
                break;
            }
            case 'discharge': {
                text += 'discharge the patient?';
                break;
            }
            case 'onlySave': {
                text += 'save form without discharging the patient?';
                break;
            }
        }
        return text;
    };

    const filterSteps = () => {
        if (!seeAndTreatForm.canDischargeFromForm) {
            if (activeStep === 2) setActiveStep(0);
            return stepper.slice(0, 2);
        }
        return stepper;
    };

    const activeStepCheck = (isForNext) => {
        if (isForNext) {
            return seeAndTreatForm.canDischargeFromForm ? activeStep < 2 : activeStep < 1;
        } else {
            return seeAndTreatForm.canDischargeFromForm ? activeStep === 2 : activeStep === 1;
        }
    };

    const addObsAndClose = () => {
        closeObsDialog();
        let note = '';

        if (isOneByOne) {
            addObservationsToExaminationInput(chosenObs, setChosenObs, setSeeAndTreatForm, 'examination');
        } else {
            chosenObs.forEach((obsNote) => {
                const tempNote = Formatter.formatObservationToText(obsNote);
                note = note !== '' ? note + '\n\n' + tempNote : tempNote;
            });

            setSeeAndTreatForm((prevState) => ({
                ...prevState,
                examination: prevState.examination !== '' ? prevState.examination + '\n\n' + note : note,
            }));
        }
    };

    const handleObsModeChange = () => {
        setOneByOne((prev) => !prev);
        setIsOneByOne((prev) => !prev);
    };

    const superFunkcja = () => {
        if (activeStep !== 1 || props.patientAgeType !== PATIENT_AGE_TYPE.PAEDS) return false;
        if (!safeguardingLevelMandatory) return false;
        return !getSafeguardingLevel(props.patient);
    };

    return (
        <>
            <div className={classes.contairner}>
                {loadingForm && <AgyleLoadingSpinner />}

                {!fullFormMode && !loadingForm && (
                    <div ref={elementRef} className={clsx(!fullFormMode && 'action-padding')}>
                        <Grid container direction="row" spacing={1}>
                            <Grid item xs={12}>
                                {patientBanner(props.patient, false)}
                            </Grid>

                            {renderClerkingClinician()}
                        </Grid>
                    </div>
                )}

                {fullFormMode && !loadingForm && (
                    <div ref={elementRef}>
                        <Grid item xs={12} sx={{ mb: 1 }}>
                            <Button
                                variant="contained"
                                color={seeAndTreatForm.canDischargeFromForm ? 'info' : 'warning'}
                                className={classes.seeAndTreatButton}
                                onClick={handleDischargeOption}
                                disabled={props.pastAttendances}
                                style={{ width: '100%', maxWidth: '100%' }}
                                fullWidth
                            >
                                {t(`Patient is ${seeAndTreatForm.canDischargeFromForm ? '' : 'not '}being discharged from See and Treat`)}
                            </Button>
                        </Grid>

                        <AgyleStepper activeStep={activeStep} steps={filterSteps()} />
                        {renderStep()}
                    </div>
                )}
            </div>

            <MessageBox
                title={t('Confirmation')}
                haveAccept={{ yes: t('Yes'), no: t('No') }}
                onAccept={handleRemoveTimestamp}
                onClose={closeRemoveTimestamp}
                message={t('Are you sure you want to remove see and treat timestamp?')}
                open={isRemoveTimestampOpen}
            />

            <MessageBox
                open={openConfirmBox}
                onClose={handleCloseMessageBox}
                title={t('Confirmation')}
                haveAccept={{ yes: t('Submit'), no: t('Close') }}
                onAccept={checkMessageBoxOption}
                message={t(checkMessageBoxMessage())}
            />

            <AgyleDialog
                title={t('Observations Comparison')}
                open={isOpenObsDialog}
                onClose={closeObsDialog}
                disableSave
                width="xl"
                buttons={[
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 3 }}>
                        <Typography>{t('Under')}</Typography>
                        <Switch checked={isOneByOne} onChange={handleObsModeChange} />
                        <Typography>{t('Side by side')}</Typography>
                    </Stack>,
                    <AgyleButton variant="contained" color="primary" onClick={addObsAndClose} className={classes.button}>
                        {t('Add and copy to clipboard')}
                    </AgyleButton>,
                ]}
            >
                <ObservationTable patient={props.patient} chooseObs chosenObs={chosenObs} setChosenObs={setChosenObs} />
            </AgyleDialog>
        </>
    );
};

export default SeeAndTreat;
